import React, { useState, useEffect, useMemo, useRef } from "react";
import EntitySelectorModal from "./EntitySelectorModal";
import HierarchyParentPickerModal from "./HierarchyParentPickerModal";
import { api } from "../services/api";
import { createDevConsole } from "../utils/devLogger";
import { parseEntityIdArray } from "../utils/entityIdArrays";
import {
  getTaxonomyHierarchyConfigByEntityType,
  isTaxonomyHierarchyType,
} from "../utils/taxonomyHierarchyConfigs";

const console = createDevConsole();

const unwrapEntity = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.id && (payload.name !== undefined || payload.type !== undefined)) {
    return payload;
  }
  if (payload.data && typeof payload.data === "object") return payload.data;
  return payload;
};

const EntityBasicInfo = ({
  mapLanguage = false,
  entityForm,
  setEntityForm,
  editingEntity,
  availableSubtypes,
  loadingSubtypes,
  readOnly,
  entityTypesOptions = [],
  onEntityTypeChange,
  /** When false, only name, type, and description are shown (create quick path). */
  showAdvancedFields = true,
  /** When set, render a control after description to reveal or hide the remaining sections. */
  onToggleAdvanced,
}) => {
  const [showSuccessorModal, setShowSuccessorModal] = useState(false);
  const [showPredecessorModal, setShowPredecessorModal] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);
  const [showChildrenModal, setShowChildrenModal] = useState(false);
  const [successorEntities, setSuccessorEntities] = useState([]);
  const [predecessorEntities, setPredecessorEntities] = useState([]);
  const [parentEntity, setParentEntity] = useState(null);
  const [childrenEntities, setChildrenEntities] = useState([]);
  const [loadingSuccessors, setLoadingSuccessors] = useState(false);
  const [loadingPredecessors, setLoadingPredecessors] = useState(false);
  const [loadingParent, setLoadingParent] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [proposingDescription, setProposingDescription] = useState(false);
  const [descriptionProposalError, setDescriptionProposalError] =
    useState(null);
  const [descriptionProposalActive, setDescriptionProposalActive] =
    useState(false);
  const descriptionBeforeProposalRef = useRef(null);
  const [discoveringAliases, setDiscoveringAliases] = useState(false);
  const [aliasProposalError, setAliasProposalError] = useState(null);
  const [aliasProposalActive, setAliasProposalActive] = useState(false);
  const aliasesBeforeProposalRef = useRef(null);

  const successorIds = useMemo(
    () => parseEntityIdArray(entityForm.successors),
    [entityForm.successors]
  );
  const predecessorIds = useMemo(
    () => parseEntityIdArray(entityForm.predecessors),
    [entityForm.predecessors]
  );
  const childrenIds = useMemo(
    () => parseEntityIdArray(entityForm.children),
    [entityForm.children]
  );

  const canProposeDescription = Boolean(editingEntity?.id) && !readOnly;

  const handleProposeDescription = async () => {
    if (!canProposeDescription || proposingDescription) return;
    const current = entityForm.description || "";
    descriptionBeforeProposalRef.current = current;
    setProposingDescription(true);
    setDescriptionProposalError(null);
    try {
      const res = await api.proposeEntityDescription(editingEntity.id, {
        currentDescription: current,
      });
      const proposed = (res?.proposed_description || "").trim();
      if (!proposed) {
        throw new Error("No description was proposed");
      }
      setEntityForm({ ...entityForm, description: proposed });
      setDescriptionProposalActive(true);
    } catch (err) {
      setDescriptionProposalActive(false);
      setDescriptionProposalError(
        err?.message || "Could not propose a description"
      );
    } finally {
      setProposingDescription(false);
    }
  };

  const handleDiscardDescriptionProposal = () => {
    const prior = descriptionBeforeProposalRef.current;
    if (prior !== null) {
      setEntityForm({ ...entityForm, description: prior });
    }
    descriptionBeforeProposalRef.current = null;
    setDescriptionProposalActive(false);
    setDescriptionProposalError(null);
  };

  const canDiscoverAliases = Boolean(editingEntity?.id) && !readOnly;

  const mergeAliasStrings = (existing, additions) => {
    const split = (raw) =>
      String(raw || "")
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const name of [...split(existing), ...split(additions)]) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    return out.join(", ");
  };

  const handleDiscoverAliases = async () => {
    if (!canDiscoverAliases || discoveringAliases) return;
    const current = entityForm.alias_names || "";
    aliasesBeforeProposalRef.current = current;
    setDiscoveringAliases(true);
    setAliasProposalError(null);
    try {
      const res = await api.discoverEntityAliases(editingEntity.id, {
        currentAliasNames: current,
      });
      const proposedText = String(
        res?.alias_names_text ||
          (Array.isArray(res?.aliases) ? res.aliases.join(", ") : "")
      ).trim();
      if (!proposedText) {
        throw new Error("No aliases were proposed");
      }
      setEntityForm({
        ...entityForm,
        alias_names: mergeAliasStrings(current, proposedText),
      });
      setAliasProposalActive(true);
    } catch (err) {
      setAliasProposalActive(false);
      setAliasProposalError(err?.message || "Could not discover aliases");
    } finally {
      setDiscoveringAliases(false);
    }
  };

  const handleDiscardAliasProposal = () => {
    const prior = aliasesBeforeProposalRef.current;
    if (prior !== null) {
      setEntityForm({ ...entityForm, alias_names: prior });
    }
    aliasesBeforeProposalRef.current = null;
    setAliasProposalActive(false);
    setAliasProposalError(null);
  };

  // Fetch successor entity details
  useEffect(() => {
    const fetchSuccessorEntities = async () => {
      if (!successorIds.length) {
        setSuccessorEntities([]);
        return;
      }
      setLoadingSuccessors(true);
      try {
        const entities = await Promise.all(
          successorIds.map((id) => api.getEntity(id))
        );
        setSuccessorEntities(entities.filter((e) => e?.id));
      } catch (error) {
        console.error("Error fetching successor entities:", error);
        setSuccessorEntities([]);
      } finally {
        setLoadingSuccessors(false);
      }
    };
    fetchSuccessorEntities();
  }, [successorIds]);

  // Fetch predecessor entity details
  useEffect(() => {
    const fetchPredecessorEntities = async () => {
      if (!predecessorIds.length) {
        setPredecessorEntities([]);
        return;
      }
      setLoadingPredecessors(true);
      try {
        const entities = await Promise.all(
          predecessorIds.map((id) => api.getEntity(id))
        );
        setPredecessorEntities(entities.filter((e) => e?.id));
      } catch (error) {
        console.error("Error fetching predecessor entities:", error);
        setPredecessorEntities([]);
      } finally {
        setLoadingPredecessors(false);
      }
    };
    fetchPredecessorEntities();
  }, [predecessorIds]);

  // Fetch parent entity details (single entity)
  useEffect(() => {
    const fetchParentEntity = async () => {
      if (!entityForm.parent) {
        setParentEntity(null);
        return;
      }
      setLoadingParent(true);
      try {
        const entity = unwrapEntity(await api.getEntity(entityForm.parent));
        setParentEntity(entity);
      } catch (error) {
        console.error("Error fetching parent entity:", error);
        setParentEntity(null);
      } finally {
        setLoadingParent(false);
      }
    };
    fetchParentEntity();
  }, [entityForm.parent]);

  const isTaxonomyType = isTaxonomyHierarchyType(entityForm.type);
  const taxonomyCfg = getTaxonomyHierarchyConfigByEntityType(entityForm.type);
  const parentDisplayName = parentEntity?.name
    ? String(parentEntity.name).trim()
    : "";

  // Fetch children entity details
  useEffect(() => {
    const fetchChildrenEntities = async () => {
      if (!childrenIds.length) {
        setChildrenEntities([]);
        return;
      }
      setLoadingChildren(true);
      try {
        const entities = await Promise.all(
          childrenIds.map((id) => api.getEntity(id))
        );
        setChildrenEntities(entities.filter((e) => e?.id));
      } catch (error) {
        console.error("Error fetching children entities:", error);
        setChildrenEntities([]);
      } finally {
        setLoadingChildren(false);
      }
    };
    fetchChildrenEntities();
  }, [childrenIds]);
  return (
    <div className="space-y-6">
      {/* Basic Information Header */}
      <div>
        <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
          {showAdvancedFields || !onToggleAdvanced
            ? "📋 Basic Information"
            : "Quick create"}
        </h2>
        {!showAdvancedFields && onToggleAdvanced ? (
          <p className="mt-2 text-sm text-gray-400">
            Name, type, and an optional description are enough to add this to
            your map.
          </p>
        ) : null}
      </div>

      {/* Two Column Layout for Basic Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Entity Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {mapLanguage ? "🏷️ Name *" : "🏷️ Entity Name *"}
            </label>
            <input
              type="text"
              placeholder={mapLanguage ? "Enter name" : "Enter entity name"}
              value={entityForm.name}
              onChange={(e) =>
                setEntityForm({ ...entityForm, name: e.target.value })
              }
              disabled={readOnly}
              className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
            />
          </div>

          {/* Subtype */}
          {showAdvancedFields ? (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              📂 Subtype
            </label>
            <div className="relative">
              <select
                value={entityForm.subtype}
                onChange={(e) =>
                  setEntityForm({ ...entityForm, subtype: e.target.value })
                }
                disabled={readOnly || loadingSubtypes}
                className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly || loadingSubtypes ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <option value="">Select subtype...</option>
                {availableSubtypes.map((subtype) => (
                  <option key={subtype.id} value={subtype.subtype_name}>
                    {subtype.subtype_name}
                  </option>
                ))}
              </select>
              {loadingSubtypes && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
            {!loadingSubtypes &&
              availableSubtypes.length === 0 &&
              entityForm.type && (
                <div className="mt-1 text-xs text-yellow-400">
                  ⚠️ No subtypes available for {entityForm.type}. Check
                  authentication or configure subtypes in Entity Configuration.
                </div>
              )}
          </div>
          ) : null}
        </div>
        {/* Right Column */}
        <div className="space-y-4">
          {/* Entity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {mapLanguage ? "🏗️ Type *" : "🏗️ Entity Type *"}
            </label>
            <select
              value={entityForm.type}
              onChange={(e) => {
                const newType = e.target.value;
                setEntityForm({ ...entityForm, type: newType });
                if (onEntityTypeChange) {
                  onEntityTypeChange(newType);
                }
              }}
              disabled={readOnly}
              className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {entityTypesOptions.length > 0 ? (
                entityTypesOptions.map((type) => {
                  const value = typeof type === "string" ? type : type.value;
                  const label =
                    typeof type === "string"
                      ? type.replace(/([A-Z])/g, " $1").trim()
                      : type.label;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })
              ) : (
                <>
                  <option value="objective">Objective</option>
                  <option value="platform">Platform</option>
                  <option value="initiative">Initiative</option>
                  <option value="organization">Organisation</option>
                  <option value="business-capability">
                    Business Capability
                  </option>
                  <option value="business-context">Business Context</option>
                  <option value="data-object">Data Object</option>
                  <option value="application">Application</option>
                  <option value="interface">Interface</option>
                  <option value="provider">Provider</option>
                  <option value="technology-component">
                    Technology Component
                  </option>
                </>
              )}
            </select>
          </div>

          {/* Status */}
          {showAdvancedFields ? (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              📌 Status
            </label>
            <select
              key={`status-${entityForm.processing_status || entityForm.status || "Processed"}-${editingEntity?.id || "new"}`}
              value={(() => {
                // Prioritize processing_status (backend field) over status
                // Only use status if processing_status is not available
                const primaryStatus =
                  entityForm.processing_status || entityForm.status;
                // Validate that the status is one of the allowed values
                const validStatuses = ["Processed", "Reviewed", "Approved"];
                if (primaryStatus && validStatuses.includes(primaryStatus)) {
                  return primaryStatus;
                }
                // If status is invalid (e.g., "active"), default to "Processed"
                return "Processed";
              })()}
              onChange={(e) => {
                const newStatus = e.target.value;
                console.log("🔄 Status field changed:", {
                  oldStatus: entityForm.processing_status || entityForm.status,
                  newStatus: newStatus,
                  entityFormStatus: entityForm.status,
                  entityFormProcessingStatus: entityForm.processing_status,
                  editingEntityId: editingEntity?.id,
                });
                setEntityForm({
                  ...entityForm,
                  status: newStatus,
                  processing_status: newStatus,
                });
              }}
              disabled={readOnly}
              className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <option value="Processed">Processed</option>
              <option value="Reviewed">Reviewed</option>
              <option value="Approved">Approved</option>
            </select>
          </div>
          ) : null}
        </div>
      </div>

      {/* Description Field - Full Width */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-300">
            {showAdvancedFields ? "📝 Description" : "📝 Description (optional)"}
          </label>
          {canProposeDescription ? (
            <button
              type="button"
              disabled={proposingDescription}
              onClick={handleProposeDescription}
              className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {proposingDescription
                ? "Working…"
                : String(entityForm.description || "").trim()
                  ? "Propose description"
                  : "Generate description"}
            </button>
          ) : null}
        </div>
        <textarea
          placeholder={
            showAdvancedFields ? "Entity description" : "Optional description"
          }
          value={entityForm.description || ""}
          onChange={(e) => {
            setEntityForm({ ...entityForm, description: e.target.value });
          }}
          rows={4}
          disabled={readOnly}
          className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
        />
        {descriptionProposalError ? (
          <p className="mt-1 text-xs text-amber-400">{descriptionProposalError}</p>
        ) : null}
        {descriptionProposalActive ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">
              Editable draft from knowledge. Edit as needed, then save the
              entity — or discard.
            </span>
            <button
              type="button"
              onClick={handleDiscardDescriptionProposal}
              className="px-2 py-0.5 text-xs font-medium text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
            >
              Discard
            </button>
          </div>
        ) : null}
      </div>

      {typeof onToggleAdvanced === "function" ? (
        <div>
          <button
            type="button"
            onClick={onToggleAdvanced}
            aria-expanded={showAdvancedFields}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`h-4 w-4 transition-transform ${
                showAdvancedFields ? "rotate-180" : ""
              }`}
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
            {showAdvancedFields ? "Hide advanced details" : "Advanced details"}
          </button>
          {!showAdvancedFields ? (
            <p className="mt-2 text-xs text-gray-400">
              Subtype, aliases, connections, and custom fields.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Alias Names Field - Full Width */}
      {showAdvancedFields ? (
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-300">
            🏷️ Alias Names
          </label>
          {canDiscoverAliases ? (
            <button
              type="button"
              disabled={discoveringAliases}
              onClick={() => void handleDiscoverAliases()}
              className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {discoveringAliases ? "Working…" : "Discover aliases"}
            </button>
          ) : null}
        </div>
        <input
          type="text"
          placeholder="Enter alternate names separated by comma or semicolon (e.g., 'Alias1, Alias2' or 'Alias1; Alias2')"
          value={entityForm.alias_names || ""}
          onChange={(e) =>
            setEntityForm((prev) => ({
              ...prev,
              alias_names: e.target.value,
            }))
          }
          disabled={readOnly}
          className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
        />
        <p className="mt-1 text-xs text-gray-400">
          Separate multiple alias names with commas (,) or semicolons (;). These
          will help AI Processing identify entities by their alternate names.
        </p>
        {aliasProposalError ? (
          <p className="mt-1 text-xs text-amber-400">{aliasProposalError}</p>
        ) : null}
        {aliasProposalActive ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">
              Draft from linked knowledge. Edit as needed, then save the entity
              — or discard.
            </span>
            <button
              type="button"
              onClick={handleDiscardAliasProposal}
              className="px-2 py-0.5 text-xs font-medium text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
            >
              Discard
            </button>
          </div>
        ) : null}
      </div>
      ) : null}

      {/* Successor and Predecessor Section */}
      {showAdvancedFields && entityForm.type && (
        <div className="space-y-4 pt-4 border-t border-gray-700">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Successor */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ➡️ Successor
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSuccessorModal(true)}
                  disabled={readOnly}
                  className={`px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm ${
                    readOnly ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {successorIds.length > 0
                    ? `Successor (${successorIds.length})`
                    : "Select Successor"}
                </button>
                {successorIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setEntityForm({ ...entityForm, successors: [] });
                    }}
                    disabled={readOnly}
                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {loadingSuccessors &&
                entityForm.successors &&
                entityForm.successors.length > 0 && (
                  <div className="mt-2 text-xs text-gray-400">Loading...</div>
                )}
              {!loadingSuccessors && successorEntities.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-gray-400 mb-1">
                    {successorEntities.length} successor
                    {successorEntities.length !== 1 ? "s" : ""}:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {successorEntities.map((entity) => (
                      <span
                        key={entity.id}
                        className="px-2 py-1 text-xs bg-purple-700 text-white rounded border border-purple-600"
                        title={entity.description || entity.name}
                      >
                        {entity.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Predecessor */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ⬅️ Predecessor
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPredecessorModal(true)}
                  disabled={readOnly}
                  className={`px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm ${
                    readOnly ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {predecessorIds.length > 0
                    ? `Predecessor (${predecessorIds.length})`
                    : "Select Predecessor"}
                </button>
                {predecessorIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setEntityForm({ ...entityForm, predecessors: [] });
                      }}
                      disabled={readOnly}
                      className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                      Clear
                    </button>
                  )}
              </div>
              {loadingPredecessors && predecessorIds.length > 0 && (
                  <div className="mt-2 text-xs text-gray-400">Loading...</div>
                )}
              {!loadingPredecessors && predecessorEntities.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-gray-400 mb-1">
                    {predecessorEntities.length} predecessor
                    {predecessorEntities.length !== 1 ? "s" : ""}:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {predecessorEntities.map((entity) => (
                      <span
                        key={entity.id}
                        className="px-2 py-1 text-xs bg-indigo-700 text-white rounded border border-indigo-600"
                        title={entity.description || entity.name}
                      >
                        {entity.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Parent and Children Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-4 border-t border-gray-700">
            {/* Parent */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ⬆️{" "}
                {isTaxonomyType && taxonomyCfg
                  ? `Parent ${taxonomyCfg.entityLabelLower}`
                  : "Parent"}
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowParentModal(true)}
                  disabled={readOnly}
                  className={`px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm ${
                    readOnly ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {entityForm.parent
                    ? isTaxonomyType
                      ? "Change parent in tree"
                      : "Change Parent"
                    : isTaxonomyType
                      ? "Pick parent from tree"
                      : "Select Parent"}
                </button>
                {entityForm.parent && (
                  <button
                    type="button"
                    onClick={() => {
                      setEntityForm({ ...entityForm, parent: "" });
                      setParentEntity(null);
                    }}
                    disabled={readOnly}
                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {loadingParent && entityForm.parent && (
                <div className="mt-2 text-xs text-gray-400">Loading parent…</div>
              )}
              {!loadingParent && entityForm.parent && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-gray-400 mb-1">Parent:</div>
                  <div className="flex flex-wrap gap-1">
                    <span
                      className="px-2 py-1 text-xs bg-green-700 text-white rounded border border-green-600"
                      title={
                        parentEntity?.description ||
                        parentDisplayName ||
                        entityForm.parent
                      }
                    >
                      {parentDisplayName || "Unknown parent"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Children */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ⬇️ Children
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowChildrenModal(true)}
                  disabled={readOnly}
                  className={`px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm ${
                    readOnly ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {childrenIds.length > 0
                    ? `Children (${childrenIds.length})`
                    : "Select Children"}
                </button>
                {childrenIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setEntityForm({ ...entityForm, children: [] });
                    }}
                    disabled={readOnly}
                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {loadingChildren && childrenIds.length > 0 && (
                  <div className="mt-2 text-xs text-gray-400">Loading...</div>
                )}
              {!loadingChildren && childrenEntities.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-gray-400 mb-1">
                    {childrenEntities.length} child
                    {childrenEntities.length !== 1 ? "ren" : ""}:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {childrenEntities.map((entity) => (
                      <span
                        key={entity.id}
                        className="px-2 py-1 text-xs bg-cyan-700 text-white rounded border border-cyan-600"
                        title={entity.description || entity.name}
                      >
                        {entity.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Entity Selector Modals */}
      <EntitySelectorModal
        isOpen={showSuccessorModal}
        onClose={() => setShowSuccessorModal(false)}
        onSelect={(selectedIds) => {
          setEntityForm({ ...entityForm, successors: selectedIds });
        }}
        currentEntityType={entityForm.type}
        currentEntityId={editingEntity?.id}
        title="Select Successor Entities"
        selectedEntityIds={successorIds}
        allowMultiple={true}
      />

      <EntitySelectorModal
        isOpen={showPredecessorModal}
        onClose={() => setShowPredecessorModal(false)}
        onSelect={(selectedIds) => {
          setEntityForm({ ...entityForm, predecessors: selectedIds });
        }}
        currentEntityType={entityForm.type}
        currentEntityId={editingEntity?.id}
        title="Select Predecessor Entities"
        selectedEntityIds={predecessorIds}
        allowMultiple={true}
      />

      {isTaxonomyType ? (
        <HierarchyParentPickerModal
          isOpen={showParentModal}
          onClose={() => setShowParentModal(false)}
          onSelect={(parentId, node) => {
            setEntityForm({
              ...entityForm,
              parent: parentId || "",
            });
            if (parentId && node) {
              setParentEntity({
                id: node.id,
                name: node.name,
                description: node.description || null,
                type: entityForm.type,
              });
            } else if (!parentId) {
              setParentEntity(null);
            }
          }}
          entityType={entityForm.type}
          currentEntityId={editingEntity?.id}
          selectedParentId={entityForm.parent || ""}
          title={
            taxonomyCfg
              ? `Select parent ${taxonomyCfg.entityLabelLower}`
              : "Select parent"
          }
        />
      ) : (
        <EntitySelectorModal
          isOpen={showParentModal}
          onClose={() => setShowParentModal(false)}
          onSelect={(selectedIds) => {
            setEntityForm({
              ...entityForm,
              parent: selectedIds.length > 0 ? selectedIds[0] : "",
            });
          }}
          currentEntityType={entityForm.type}
          currentEntityId={editingEntity?.id}
          title="Select Parent Entity"
          selectedEntityIds={entityForm.parent ? [entityForm.parent] : []}
          allowMultiple={false}
        />
      )}

      <EntitySelectorModal
        isOpen={showChildrenModal}
        onClose={() => setShowChildrenModal(false)}
        onSelect={(selectedIds) => {
          setEntityForm({ ...entityForm, children: selectedIds });
        }}
        currentEntityType={entityForm.type}
        currentEntityId={editingEntity?.id}
        title="Select Children Entities"
        selectedEntityIds={childrenIds}
        allowMultiple={true}
      />
    </div>
  );
};

export default EntityBasicInfo;
